import { Alert, Card, LinkButton, Modal } from '@pmi/dsm-react';
import { ArrowBackIcon } from '@pmi/dsm-react/dist/components/icons/24';
import IconDanger from '@pmi/dsm-react/dist/components/icons/16/CircleRemoveIcon';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { CardBody } from 'react-bootstrap';
import { reportError } from '../../../../../shared/analytics';
import SkeletonLoader from '../../../../../shared/components/SkeletonLoader';
import usePermissions from '../../../../../shared/hooks/usePermissions';
import { User } from '../../../../../shared/models/UserModels';
import PERMISSIONS from '../../../../../shared/permissions';
import { accountSelector } from '../../../../../shared/store/shared/accountSlice';
import { useGetDealsListQuery } from '../../../../../shared/store/shared/dealsApiSlice';
import editModalStyles from '../EditModal/editModal.module.scss';
import styles from './viewUserModal.module.scss';
import UserDetailsCard from './UserDetailsCard';
import { useGetUserEnrollmentsQuery } from './UserEnrollmentList/apiSlice';
import UserCurrentProductsTable from './UserCurrentProductsTable';
import PreviousProductLogTable from './PreviousProductLogTable';
import { useGetUserCurrentProductsQuery } from './UserCurrentProductsTable/apiSlice';

interface ViewUserModalProps {
  user: User;
  onAddProduct: (user: User) => void;

  onCancel: () => void;
  onDisable: (user: User) => void;
  onRestore: (user: User) => void;
}

export default function ViewUserModal({
  user,
  onAddProduct,
  onDisable,
  onRestore,
  onCancel = () => {},
}: ViewUserModalProps) {
  const { currentAccountId } = useSelector(accountSelector);
  const { hasAccess, isLoading: permissionLoading } = usePermissions();
  const { data: enrollmentData } = useGetUserEnrollmentsQuery(
    { accountId: currentAccountId, personId: user.personId },
    { skip: !user.personId },
  );

  const {
    data: deals,
    isLoading,
    isFetching,
    isError,
  } = useGetDealsListQuery(currentAccountId, { skip: permissionLoading });
  const errorMessage = 'Failed to load agreements';

  const {
    data: userAssignedProducts,
    isLoading: isUserAssignedProductsLoading,
    isFetching: isUserAssignedProductsFetching,
  } = useGetUserCurrentProductsQuery({
    userId: user.id.toString(),
  });

  const accessibleDeals = useMemo(
    () =>
      deals?.filter(() =>
        hasAccess({ permissions: [PERMISSIONS.ManageDealUsers] }),
      ) ?? [],
    [isLoading, isFetching, permissionLoading],
  );

  const hasRedemption = useMemo(
    () => !!userAssignedProducts?.some((up) => up?.quantityRedeemed > 0),
    [userAssignedProducts],
  );

  useEffect(() => {
    if (isError) reportError(errorMessage);
  }, [isError]);

  // Animation workaround. Remove, when DSM supported
  const [animationClassName, setAnimationClassName] = useState('hidden');

  useEffect(() => {
    setTimeout(() => setAnimationClassName(''), 0);
  }, []);

  return (
    <Modal
      show
      backdrop="static"
      customClassName={`m-0 ${editModalStyles.editModal} viewUserModal ${animationClassName}`}
      variant="input"
      onClose={onCancel}
      data-testid="view-user-modal"
    >
      <div className="wizard-container pb-5">
        {isError && (
          <Alert variant="danger" alertText={errorMessage} icon={IconDanger} />
        )}
        <LinkButton
          size="sm"
          className="mb-4"
          titleText="Back to people"
          variant="outline-secondary"
          icon={ArrowBackIcon}
          onClick={onCancel}
        />
        {(isLoading ||
          isFetching ||
          isUserAssignedProductsLoading ||
          isUserAssignedProductsFetching) && <SkeletonLoader />}
        {!isLoading &&
          !isFetching &&
          !isError &&
          !isUserAssignedProductsLoading &&
          !isUserAssignedProductsFetching && (
            <div className="text-start mb-2">
              <UserDetailsCard
                user={user}
                onRestore={onRestore}
                onCancelViewModal={onCancel}
                onDisable={onDisable}
                hasEnrollments={
                  enrollmentData?.filter((x) => x.orderStatus !== 'Refunded')
                    .length > 0
                }
                hasRedemption={hasRedemption}
              />
              <Card
                className={`d-block mt-md-5 mt-4 mb-2 ${styles.modalUserDetailsCardBlock} shadow-sm`}
              >
                <CardBody>
                  <UserCurrentProductsTable
                    isLoading={
                      isUserAssignedProductsLoading ||
                      isUserAssignedProductsFetching
                    }
                    accessibleAgreements={accessibleDeals}
                    list={userAssignedProducts ?? []}
                    user={user}
                    onAddProduct={onAddProduct}
                  />
                </CardBody>
              </Card>
              {!user.isRemoved && (
                <Card
                  className={`d-block mt-md-5 mt-4 mb-2 ${styles.modalUserDetailsCardBlock} shadow-sm`}
                >
                  <CardBody>
                    <PreviousProductLogTable
                      user={user}
                      isLoading={isLoading || isFetching}
                      accessibleAgreements={accessibleDeals}
                      productsList={[]}
                    />
                  </CardBody>
                </Card>
              )}
            </div>
          )}
      </div>
    </Modal>
  );
}
